import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
    { id: 'home',       label: 'Home'       },
    { id: 'projects',   label: 'Work'       },
    { id: 'experience', label: 'Experience' },
    { id: 'skills',     label: 'Expertise'  },
    { id: 'writing',    label: 'Writing',   route: '/blog' },
    { id: 'mirasignal', label: 'MiraSignal', route: '/mirasignal' },
    { id: 'about',      label: 'About'      },
    { id: 'contact',    label: 'Contact'    },
];

const Header = ({ darkTheme, toggleTheme, isBlogPage = false }) => {
    const [menuOpen, setMenuOpen]           = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const navigate   = useNavigate();
    const location   = useLocation();

    const onHomeRoute = location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            const header = document.getElementById('header');
            if (window.scrollY >= 50) {
                header.classList.add('shadow-header');
            } else {
                header.classList.remove('shadow-header');
            }

            // Close mobile menu when user scrolls.
            setMenuOpen(false);

            if (!onHomeRoute) return;

            const sections = document.querySelectorAll('section[id]');
            sections.forEach((section) => {
                const sectionTop    = section.offsetTop - 80;
                const sectionHeight = section.offsetHeight;
                const scrollY       = window.pageYOffset;
                if (scrollY > sectionTop && scrollY <= sectionTop + sectionHeight) {
                    setActiveSection(section.getAttribute('id'));
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [onHomeRoute]);

    const handleNavClick = (item) => {
        setMenuOpen(false);
        if (item.route) {
            navigate(item.route);
            return;
        }
        // If on an inner page, go home first then scroll.
        if (!onHomeRoute) {
            navigate('/');
            // Scroll happens after navigation; use a small delay.
            setTimeout(() => {
                const element = document.getElementById(item.id);
                if (element) element.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            return;
        }
        const element = document.getElementById(item.id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <header className="header" id="header">
            <nav className="nav container">
                {/* Logo */}
                <a
                    href="/"
                    className="nav__logo"
                    onClick={(e) => {
                        e.preventDefault();
                        if (!onHomeRoute) { navigate('/'); } else { handleNavClick({ id: 'home' }); }
                    }}
                >
                    <span className="nav__logo-circle">H</span>
                    <span className="nav__logo-name">Haider Masood.</span>
                </a>

                {/* Mobile + desktop menu */}
                <div className={`nav__menu ${menuOpen ? 'show-menu' : ''}`} id="nav-menu">
                    <span className="nav__title">Menu</span>
                    <h1 className="nav__name">Haider Masood</h1>

                    <ul className="nav__list">
                        {NAV_ITEMS.map((item) => {
                            const isWriting = item.route === '/blog';
                            const isRouteItem = Boolean(item.route);
                            const isActive = isRouteItem
                                ? location.pathname.startsWith(item.route)
                                : (onHomeRoute && activeSection === item.id);
                            return (
                                <li className="nav__item" key={item.id}>
                                    <a
                                        href={item.route || `#${item.id}`}
                                        className={[
                                            'nav__link',
                                            isActive           ? 'active-link'      : '',
                                            item.id === 'contact' && !isWriting ? 'nav__link-button' : '',
                                        ].join(' ')}
                                        onClick={(e) => { e.preventDefault(); handleNavClick(item); }}
                                    >
                                        {item.label}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>

                    <button
                        type="button"
                        className="nav__close"
                        onClick={() => setMenuOpen(false)}
                        aria-label="Close menu"
                    >
                        <i className="ri-close-line" aria-hidden="true"></i>
                    </button>
                </div>

                {/* Theme toggle + hamburger */}
                <div className="nav__buttons">
                    <i
                        className={`ri-${darkTheme ? 'sun' : 'moon'}-line change-theme`}
                        title={darkTheme ? 'Switch to light mode' : 'Switch to dark mode'}
                        onClick={toggleTheme}
                    ></i>
                    <button
                        type="button"
                        className="nav__toggle"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-controls="nav-menu"
                        aria-expanded={menuOpen}
                    >
                        <i className="ri-menu-4-line" aria-hidden="true"></i>
                    </button>
                </div>
            </nav>
        </header>
    );
};

export default Header;
